




















































































import {computed, defineComponent, PropType, reactive, ref, watch} from "@vue/composition-api";
import useVuelidate from "@vuelidate/core";
import {required, url} from "@vuelidate/validators";

import {
    BuilderBuildFromInterface,
    BuilderParameterInterface,
    BuilderResult,
    BuilderSection,
    BuilderTemplateInterface,
    BuilderUrlInterface,
    ProjectBuilderData
} from "@tool/Builder/Builder.types";

import ThemeBlock from "@base/Components/theme/ThemeBlock.vue";
import ThemeBlockHeaderDropdownOption from "@base/Components/theme/ThemeBlockHeaderDropdownOption.vue";
import BuilderResultsGroup from "@tool/Builder/sections/BuilderResultsGroup.vue";
import BuilderInitGuest from "@tool/Builder/init/BuilderInitGuest";
import BuilderInitUser from "@tool/Builder/init/BuilderInitUser";
import BuilderSectionCTA from "@tool/Builder/sections/BuilderSectionCTA.vue";
import BuilderSectionGuide from "@tool/Builder/sections/BuilderSectionGuide.vue";
import BuilderSectionHistory from "@tool/Builder/sections/BuilderSectionHistory.vue";
import BuilderSectionOptions from "@tool/Builder/sections/BuilderSectionOptions.vue";
import BuilderSectionShare from "@tool/Builder/sections/BuilderSectionShare.vue";
import BuilderSectionWarningStorage from "@tool/Builder/sections/BuilderSectionWarningStorage.vue";
import BuilderSectionWorksGreatWith from "@tool/Builder/sections/BuilderSectionWorksGreatWith.vue";
import SelectDropdown from "@tool/Components/SelectDropdown.vue";
import {SelectDropdownInitTemplate} from "@tool/Components/SelectDropdownInit";
import SelectParametersGroup from "@tool/Components/SelectParametersGroup.vue";
import Alert from "@base/Components/Alert.vue";
import useBuilderStore from "@tool/store/useBuilderStore";
import ModalSaveAsATemplate from "@tool/Builder/Modals/ModalSaveAsATemplate.vue";
import ModalNeedToRegister from "@tool/Builder/Modals/ModalNeedToRegister.vue";
import VueScrollTo from 'vue-scrollto';
import {ParameterType, TemplateEntityInterface} from "@tool/tool.types";
import useRequiresUpgradeAlerts from "@tool/Helpers/useRequiresUpgradeAlerts";

export default defineComponent({
    components: {
        ThemeBlock,
        ThemeBlockHeaderDropdownOption,
        BuilderResultsGroup,
        BuilderSectionCTA,
        BuilderSectionGuide,
        BuilderSectionHistory,
        BuilderSectionOptions,
        BuilderSectionShare,
        BuilderSectionWarningStorage,
        BuilderSectionWorksGreatWith,
        SelectDropdown,
        SelectParametersGroup,
        Alert,
        ModalSaveAsATemplate,
        ModalNeedToRegister
    },
    props: {
        buildFrom: {
            type: Object as PropType<BuilderBuildFromInterface>,
            default: () => ({})
        },
        projectBuilderData: {
            type: Object as PropType<ProjectBuilderData>,
            required: false,
        },
        devMode: {
            type: Boolean,
            default: false,
        }
    },
    setup(props) {

        /**
         * Init data
         * Get data either from local storage or database based on projectData
         */
        props.projectBuilderData ? BuilderInitUser(props.projectBuilderData) : BuilderInitGuest();

        const {
            project,

            formDestinationUrl,
            formParameters,

            projectUrls,
            projectTemplates,
            projectBuilderSettings,
            projectPermissions,

            maximizers,

            showModalNeedToRegister,

            selectParameter,
            setParameters,

            touchUrl,

        } = useBuilderStore();


        const title = project.value === null ? 'Experience a Smarter Way to Work with UTM Parameters' : 'UTM Builder';

        /**
         * Form
         */
        const validator = useVuelidate({
            url: {
                required, url,
            },
            utm_source: {
                required,
            }
        }, {
            url: formDestinationUrl,
            utm_source: formParameters.value.utm_source
        });

        const formResult = reactive<BuilderResult>({
            full_url: '',
            saved_url_model: null,
        });


        /**
         * Url
         */
        const hasChangedUrl = ref(false);

        const stateUrl = computed(() => {
            return hasChangedUrl.value === false ? null : !validator.value.url.$invalid;
        });

        const onChangedUrl = async () => {
            hasChangedUrl.value = true;

            formDestinationUrl.value = formDestinationUrl.value.replace(/\n/g, "");

            formDestinationUrl.value.replace(/\n/g, "");

            if (formDestinationUrl.value !== '' && formDestinationUrl.value.substring(0, 4) !== 'http') {
                formDestinationUrl.value = 'http://' + formDestinationUrl.value;
            }

            await validator.value.url.$touch();

            buildUrl();
        }

        /**
         * Parameters
         */

        const isSelectedAtLeastOneParameter = computed(() => {
            return formParameters.value.utm_source !== null ||
                    formParameters.value.utm_campaign !== null ||
                    formParameters.value.utm_content !== null ||
                    formParameters.value.utm_medium !== null ||
                    formParameters.value.utm_term !== null;
        });

        watch(formParameters, () => {
            buildUrl();
            checkIfTemplateExists();
        }, {deep: true});


        /**
         * Template
         */
        const selectTemplateProps = SelectDropdownInitTemplate(projectTemplates.value);
        const selectTemplateValue = ref<BuilderTemplateInterface | null>(null);
        const showModalTemplateCreateOrUpdate = ref(false);

        const onOpenModalTemplateCreate = () => {

            if (project.value === null) {
                showModalNeedToRegister.value = true;
                return;
            }

            if (!projectPermissions.value.canManageShorturls) {
                return useRequiresUpgradeAlerts().shorturlsManagement(project.value!.team_id);
            }

            showModalTemplateCreateOrUpdate.value = true;
        }

        const checkIfTemplateExists = () => {
            const selectedParams = Object.entries(formParameters.value)
                    .reduce((a: BuilderParameterInterface[], [key, val]) => {
                        if (val)
                            a.push(val);
                        return a;
                    }, []);

            const existingTemplate = projectTemplates.value.find(template => {
                if (template.parameters.length !== selectedParams.length) {
                    return false;
                }

                for (let i = 0; i < selectedParams.length; i++) {
                    const paramInTemplate = template.parameters.find(templateParam =>
                            templateParam.name === selectedParams[i].name &&
                            templateParam.type === selectedParams[i].type
                    );

                    if (!paramInTemplate) {
                        return false;
                    }
                }
                return true;
            });

            selectTemplateValue.value = existingTemplate ?? null;
        }

        const onSelectedTemplate = (template: TemplateEntityInterface) => {
            selectTemplateValue.value = template;
            setParameters(template.parameters.map(param => ({...param, notes: null})));
        }

        /**
         * Builder
         */
        const buildUrl = () => {
            if (formDestinationUrl.value === '' || formParameters.value.utm_source === null) {
                if (formResult.full_url !== '') {
                    formResult.full_url = '';
                    formResult.saved_url_model = null;
                }

                return;
            }

            let queryString = "utm_source=" + (formParameters.value.utm_source ? formParameters.value.utm_source.name : '');

            if (formParameters.value.utm_medium !== null) {
                queryString += "&utm_medium=" + formParameters.value.utm_medium.name;
            }

            if (formParameters.value.utm_campaign !== null) {
                queryString += "&utm_campaign=" + formParameters.value.utm_campaign.name;
            }

            if (formParameters.value.utm_term !== null) {
                queryString += "&utm_term=" + formParameters.value.utm_term.name;
            }

            if (formParameters.value.utm_content !== null) {
                queryString += "&utm_content=" + formParameters.value.utm_content.name;
            }

            // Only if urls do not match
            let newUrl = formDestinationUrl.value + (formDestinationUrl.value.indexOf("?") > -1 ? "&" : "?") + queryString;

            if (formResult.full_url !== newUrl) {

                // Find existing url
                const savedUrlModel = projectUrls.value.find(projectUrl => projectUrl.full_url === newUrl);

                if (savedUrlModel) {
                    touchUrl(savedUrlModel);
                }

                formResult.full_url = newUrl;
                formResult.saved_url_model = savedUrlModel ?? null;
            }
        }


        /**
         * Current Section Related
         */
        const currentSection = ref<BuilderSection>('start');

        const clickOnBuilderSection = (section: BuilderSection) => {
            currentSection.value = section;
        }

        const scrollToBuilder = () => {
            VueScrollTo.scrollTo("#builder", 1000, {
                offset: -20
            });
        }


        /**
         * Actions
         */
        const actionOnBuildSimilar = (url: BuilderUrlInterface) => {

            console.log('url', url);
            formDestinationUrl.value = url.destination_url;

            const templateParameterNames: ParameterType[] = [];
            for (let i = 0; i < url.parameters.length; i++) {
                templateParameterNames.push(url.parameters[i].type);
                formParameters.value[url.parameters[i].type] = url.parameters[i];
            }

            // Remove params that are not set
            const paramKeys = Object.keys(formParameters.value) as ParameterType[];

            for (let i = 0; i < paramKeys.length; i++) {
                if (!templateParameterNames.includes(paramKeys[i]) && formParameters.value[paramKeys[i]] !== null) {
                    formParameters.value[paramKeys[i]] = null;
                }
            }

            formResult.saved_url_model = url;
            formResult.full_url = url.full_url;

            scrollToBuilder();
        }

        const onSavedUrl = (url: BuilderUrlInterface) => {
            formResult.saved_url_model = url;
        }

        /**
         * Build from
         */
        if (props.buildFrom.url) {
            formDestinationUrl.value = props.buildFrom.url.destination_url;
            setParameters(props.buildFrom.url.parameters);
        }

        if (props.buildFrom.template) {
            setParameters(props.buildFrom.template.parameters.map(param => ({...param, notes: null})));
        }


        return {
            title,
            project,

            // Maximizers
            maximizers,

            // Form
            formDestinationUrl,
            formParameters,
            formResult,

            // Url
            stateUrl,
            onChangedUrl,

            // Parameters
            isSelectedAtLeastOneParameter,

            // Template
            selectTemplateProps,
            selectTemplateValue,
            onOpenModalTemplateCreate,
            showModalTemplateCreateOrUpdate,
            onSelectedTemplate,

            // Settings
            projectBuilderSettings,

            // Current Section related
            currentSection,
            clickOnBuilderSection,
            scrollToBuilder,

            // Actions
            actionOnBuildSimilar,
            onSavedUrl,

            //
            showModalNeedToRegister
        }
    }
});
